import type { Universe } from '@/types/universe';
import type { AddRevealRef } from '@/hooks/useScrollReveal';

interface FeaturesProps {
  universe: Universe;
  addRevealRef: AddRevealRef;
}

const serif = { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 };

export default function Features({ universe, addRevealRef }: FeaturesProps) {
  return (
    <section id="deliverables" className="relative overflow-hidden bg-bg-dark py-24 md:py-32 px-6 md:px-10">
      {/* Vidéo de fond */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
        src="/assets/images/video_background.mp4" />
      {/* Overlay sombre — garde le texte blanc lisible */}
      <div className="absolute inset-0 z-[1] bg-black/70" />

      <div className="relative z-10 max-w-7xl mx-auto pt-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
          <div>
            <p className="flex items-center gap-3 text-white/60 text-[11px] font-semibold uppercase tracking-widest mb-4">
              <span className="inline-block w-6 h-px bg-accent" />
              Ce que vous réalisez
            </p>
            <h2
              ref={addRevealRef}
              className="reveal text-white text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none"
              style={serif}>
              {universe.featuresTitle}<br />
              <span className="italic opacity-70">{universe.featuresTitleAccent}</span>
            </h2>
          </div>
          <p className="text-white/70 text-base leading-relaxed max-w-xs font-light">
            {universe.featuresDesc}
          </p>
        </div>

        {/* Une seule grande carte */}
        <div
          ref={addRevealRef}
          className="reveal reveal-delay-1 rounded-3xl p-10 md:p-16 flex flex-col justify-end min-h-[300px] max-w-2xl bg-white/[0.08] border border-white/[0.12]">
          <div className="flex flex-col gap-10 max-w-md">
            {universe.features.map((feat, i) =>
              <div key={i}>
                {i > 0 && <div className="mb-10 h-px bg-white/10" />}
                <span className="text-accent text-[10px] uppercase tracking-widest font-semibold">{feat.tag}</span>
                <h3 className="text-white text-3xl mt-2 mb-3 leading-tight" style={serif}>
                  {feat.title}
                </h3>
                <p className="text-white/[0.65] text-sm leading-relaxed font-light">{feat.desc}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}